import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, CheckCircle, Copy } from 'lucide-react';

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [copiedItem, setCopiedItem] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulated submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1000);
  };

  const copyToClipboard = async (type) => {
    let textToCopy = '';
    
    if (type === 'subject') {
      textToCopy = `Contact Form: ${formData.subject}`;
    } else if (type === 'body') {
      textToCopy = `Dear Enes,

I hope this message finds you well.

I am writing to you regarding: ${formData.subject}

Please find the relevant details below:

**Contact Information:**
• Name: ${formData.name}
• Email: ${formData.email}

**Message:**
${formData.message}

I would appreciate it if you could get back to me as soon as possible.

Thank you for your time and consideration. I look forward to your response.

Best regards,
${formData.name}`;
    }

    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      setCopiedItem(type);
      setTimeout(() => {
        setIsCopied(false);
        setCopiedItem('');
      }, 2000);
    } catch (err) {
      console.error('Copy error:', err);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: 'enes.ucar.2535@gmail.com',
      description: 'We\'ll respond within 24 hours'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-slate-900 mb-6"
          >
            Get in Touch
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-slate-600 max-w-3xl mx-auto"
          >
            Have questions about our courses or need help getting started? 
            We're here to help! Reach out to us and we'll get back to you as soon as possible.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h2>
            
            {isSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Email Ready! 📧</h3>
                
                {/* Email Subject */}
                <div className="bg-blue-50 rounded-lg p-4 mb-4 text-left">
                  <h4 className="font-semibold text-blue-900 mb-2">📌 Email Subject:</h4>
                  <div className="bg-white rounded p-3 text-sm text-slate-700 mb-3">
                    Contact Form: {formData.subject}
                  </div>
                  <button
                    onClick={() => copyToClipboard('subject')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                      isCopied && copiedItem === 'subject'
                        ? 'bg-green-600 text-white' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    <Copy className="w-4 h-4" />
                    <span>{isCopied && copiedItem === 'subject' ? 'Copied!' : 'Copy Subject'}</span>
                  </button>
                </div>

                {/* Email Body */}
                <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
                  <h4 className="font-semibold text-green-900 mb-2">📝 Email Body:</h4>
                  <div className="bg-white rounded p-3 text-sm text-slate-700 whitespace-pre-line mb-3 font-mono">
                    Dear Enes,

I hope this message finds you well.

I am writing to you regarding: {formData.subject}

Please find the relevant details below:

<span className="font-bold">Contact Information:</span>
• Name: {formData.name}
• Email: {formData.email}

<span className="font-bold">Message:</span>
{formData.message}

I would appreciate it if you could get back to me as soon as possible.

Thank you for your time and consideration. I look forward to your response.

Best regards,
{formData.name}
                  </div>
                  <button
                    onClick={() => copyToClipboard('body')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                      isCopied && copiedItem === 'body'
                        ? 'bg-green-600 text-white' 
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    <Copy className="w-4 h-4" />
                    <span>{isCopied && copiedItem === 'body' ? 'Copied!' : 'Copy Email Body'}</span>
                  </button>
                </div>

                <div className="mt-6 text-sm text-slate-600">
                  <p><strong>📋 How to send:</strong></p>
                  <p>1. Copy the subject above</p>
                  <p>2. Copy the email body above</p>
                  <p>3. Open your email app</p>
                  <p>4. Send to: <strong>enes.ucar.2535@gmail.com</strong></p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                    placeholder="What's this about?"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 resize-none"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
              <p className="text-slate-600 mb-8">
                We're here to help and answer any questions you might have. 
                We look forward to hearing from you.
              </p>
            </div>

            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                  className="flex items-start space-x-4"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <info.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">{info.title}</h3>
                    <p className="text-blue-600 font-medium mb-1">{info.details}</p>
                    <p className="text-slate-600 text-sm">{info.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* FAQ Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Frequently Asked Questions</h3>
              <div className="space-y-3 text-sm text-slate-600">
                <p><strong>Q:</strong> How long does it take to get a response?</p>
                <p><strong>A:</strong> We typically respond within 24 hours during business days.</p>
                <p><strong>Q:</strong> Can I get a refund if I'm not satisfied?</p>
                <p><strong>A:</strong> Yes, we offer a 30-day money-back guarantee.</p>
                <p><strong>Q:</strong> Do you offer technical support?</p>
                <p><strong>A:</strong> Absolutely! We provide comprehensive technical support for all our courses.</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default ContactPage;
